<script setup>
import { i18n } from 'boot/i18n';
import UniversalTooltip from 'components/UI/UniversalTooltip.vue';
import {
  HrActivitySpinner,
  HrConfirmBtn,
  HrDestructiveBtn,
  HrDismissBtn,
  HrFilterChipGroup,
  HrIcon,
  HrIconActionBtn,
  HrIconToggleBtn,
  HrP,
  HrTextField,
  HrWarningBanner,
  notifyNegative,
  notifySuccess,
} from 'src/design-system';
import { computed, ref, watch } from 'vue';

import { VERDICT_OPTIONS, VERDICTS } from '../../domain/constants/auditResults';
import { ACTIONABLE_LEARNING_DECISIONS } from '../../domain/constants/auditLearnings';
import { CHECKOUT_AUDIT_FAILURE_REASONS } from '../../domain/constants/checkoutAuditStatuses';
import { isAuditRunInFlight, isLocalAuditRunCurrent } from '../../domain/services/auditRunMarker';
import { useAuditFeedback } from '../composables/useAuditFeedback';
import { useAuditLearningProposal } from '../composables/useAuditLearningProposal';
import { useAuditRunClock } from '../composables/useAuditRunClock';
import { useAuditRunState } from '../composables/useAuditRunState';
import { useAuditorAccess } from '../composables/useAuditorAccess';
import AuditVerdictBanner from './AuditVerdictBanner.vue';

defineOptions({ name: 'AuditResultCard' });

const emit = defineEmits(['refresh']);

const props = defineProps({
  sessionActivity: {
    type: Object,
    required: true,
  },
});

const { canManageAuditorBehavior } = useAuditorAccess();
const { forgetAuditRun, getAuditRun } = useAuditRunState();
const now = useAuditRunClock();

const auditRun = computed(() => getAuditRun(props.sessionActivity?.id));

const isAuditRunning = computed(() => isAuditRunInFlight(auditRun.value, props.sessionActivity, now.value));

const hasStoredVerdict = computed(() => !!props.sessionActivity?.audit?.verdict);

// The run ends before its verdict reaches the activity, so feedback in between would hit the old one.
const isStoredResultCurrent = computed(() => {
  const { sessionActivityAuditId } = auditRun.value;

  return !sessionActivityAuditId
    || !isLocalAuditRunCurrent(auditRun.value, props.sessionActivity)
    || sessionActivityAuditId === props.sessionActivity?.audit?.sessionActivityAuditId;
});

const canRateAudit = computed(() => (
  canManageAuditorBehavior.value
  && hasStoredVerdict.value
  && !isAuditRunning.value
  && isStoredResultCurrent.value
));

const {
  isProcessing: isProcessingFeedback, hasFeedback, markAccurate, markInaccurate, removeAuditResult,
} = useAuditFeedback(() => props.sessionActivity);

const {
  isProcessing: isProcessingLearningProposal,
  proposal,
  hasProposal,
  isNewProposal,
  isUpdateProposal,
  isDeleteProposal,
  editLearningValue,
  rules,
  hasLearningValidationError,
  isMatchedLearningValid,
  setProposal,
  dismissProposal,
  approveNewProposal,
  approveUpdateProposal,
  approveDeleteProposal,
} = useAuditLearningProposal(() => props.sessionActivity);

const isShowingInaccurateFeedbackForm = ref(false);
const feedbackComment = ref('');
const feedbackCorrectedVerdict = ref(null);

const VERDICT_TONES = {
  [VERDICTS.FAIL]: 'error',
  [VERDICTS.FLAG]: 'warning',
  [VERDICTS.PASS]: 'success',
};

const FAILED_RUN_PRESENTATION = {
  [CHECKOUT_AUDIT_FAILURE_REASONS.ERROR]: {
    icon: 'running_with_errors',
    labelKey: 'noteAuditor.statusErrored',
    messageKey: 'noteAuditor.feedback.auditFailedMessage',
  },
  [CHECKOUT_AUDIT_FAILURE_REASONS.UNAVAILABLE]: {
    icon: 'cloud_off',
    labelKey: 'noteAuditor.statusUnavailable',
    messageKey: 'noteAuditor.feedback.auditUnreachableMessage',
  },
};

const correctedVerdictOptions = computed(() => {
  if (!props.sessionActivity.audit) {
    return [];
  }

  return VERDICT_OPTIONS.map((option) => ({
    value: option.value,
    label: i18n.t(option.labelKey),
  }));
});

// Last resort: a stored verdict outlives a run that failed to replace it.
const failedRunBannerProps = computed(() => {
  const failure = FAILED_RUN_PRESENTATION[auditRun.value.failureReason];

  if (!failure) {
    return null;
  }

  return {
    tone: 'warning',
    statusIcon: failure.icon,
    statusLabel: i18n.t(failure.labelKey),
    message: i18n.t(failure.messageKey),
  };
});

const auditResultBannerProps = computed(() => {
  if (isAuditRunning.value) {
    return {
      tone: 'info',
      statusIcon: 'hourglass_top',
      statusLabel: i18n.t('noteAuditor.statusRunning'),
      message: i18n.t(hasStoredVerdict.value
        ? 'noteAuditor.feedback.reauditRunningMessage'
        : 'noteAuditor.feedback.auditRunningMessage'),
    };
  }

  if (!props.sessionActivity?.audit) {
    return failedRunBannerProps.value;
  }

  const { verdict, rationale } = props.sessionActivity.audit;
  const verdictOption = VERDICT_OPTIONS.find((option) => option.value === verdict);
  const tone = VERDICT_TONES[verdict];

  if (!verdictOption || !tone) {
    return null;
  }

  return {
    tone,
    statusIcon: verdictOption.icon,
    message: rationale,
    statusLabel: i18n.t(`noteAuditor.feedback.verdictStatus.${verdict}`),
  };
});

const learningProposalSection = computed(() => {
  if (isDeleteProposal.value) {
    return {
      icon: 'delete',
      containerClass: 'hr-error-container',
      heading: i18n.t('noteAuditor.feedback.learningSections.deleteRule'),
    };
  }

  if (isUpdateProposal.value) {
    return {
      icon: 'edit',
      containerClass: 'hr-tertiary-container',
      heading: i18n.t('noteAuditor.feedback.learningSections.updateRule'),
    };
  }

  if (isNewProposal.value) {
    return {
      icon: 'tips_and_updates',
      containerClass: 'hr-tertiary-container',
      heading: i18n.t('noteAuditor.feedback.learningSections.newRule'),
    };
  }

  return null;
});

const learningProposalTitle = computed(() => {
  if (isNewProposal.value) {
    return proposal.value?.proposedLearning?.headline || i18n.t('noteAuditor.feedback.learningHeading');
  }

  if (isUpdateProposal.value) {
    return proposal.value?.updatedLearning?.headline || i18n.t('noteAuditor.feedback.learningHeading');
  }

  if (isDeleteProposal.value) {
    return i18n.t('noteAuditor.feedback.deleteLearningHeading');
  }

  return i18n.t('noteAuditor.feedback.learningHeading');
});

const learningProposalReasoning = computed(() => {
  if (isNewProposal.value) {
    return proposal.value?.proposedLearning?.reasoning || null;
  }

  if (isUpdateProposal.value) {
    return proposal.value?.updatedLearning?.reasoning || null;
  }

  if (isDeleteProposal.value) {
    return proposal.value?.deletionReasoning || null;
  }

  return null;
});

const learningProposalReasoningLabel = computed(() => {
  if (isDeleteProposal.value) {
    return i18n.t('noteAuditor.feedback.whyRemoveLabel');
  }

  if (isUpdateProposal.value) {
    return i18n.t('noteAuditor.feedback.whyThisChangeLabel');
  }

  return i18n.t('noteAuditor.feedback.whyThisRuleLabel');
});

/**
 * Resets the inaccurate feedback form and hides it.
 */
function cancelInaccurateFeedbackForm() {
  feedbackComment.value = '';
  feedbackCorrectedVerdict.value = null;
  isShowingInaccurateFeedbackForm.value = false;
} // cancelInaccurateFeedbackForm()

// Feedback landing with a re-run would attach to the new audit while describing the old verdict.
watch(isAuditRunning, (isRunning) => {
  if (isRunning) {
    cancelInaccurateFeedbackForm();
  }
});

/**
 * Submits accurate feedback for the audit result, marking it as accurate.
 * Shows a success or error notification based on the outcome.
 */
async function submitAccurateFeedback() {
  try {
    await markAccurate();
    emit('refresh');
    notifySuccess(i18n.t('noteAuditor.feedback.notifyAccurateSuccess'));
  } catch (err) {
    console.error('Error submitting feedback:', err);
    notifyNegative(i18n.t('noteAuditor.feedback.notifyFeedbackFail'));
  }
} // submitAccurateFeedback()

/**
 * Displays the form for submitting inaccurate feedback, allowing the user to provide a
 * comment and corrected verdict.
 */
function switchInaccurateFeedbackForm() {
  if (isShowingInaccurateFeedbackForm.value) {
    cancelInaccurateFeedbackForm();
    return;
  }
  isShowingInaccurateFeedbackForm.value = true;
} // switchInaccurateFeedbackForm()

/**
 * Submits inaccurate feedback for the audit result, including the user's comment and corrected verdict.
 * Shows a success or error notification based on the outcome, and refreshes the audit result.
 */
async function submitInaccurateFeedback() {
  try {
    const feedbackLearningOutcome = await markInaccurate(
      feedbackComment.value,
      feedbackCorrectedVerdict.value,
    );

    cancelInaccurateFeedbackForm();
    emit('refresh');

    if (ACTIONABLE_LEARNING_DECISIONS.includes(feedbackLearningOutcome?.learningDecision)) {
      setProposal(feedbackLearningOutcome);
      return;
    }

    notifySuccess(i18n.t('noteAuditor.feedback.notifyInaccurateSuccess'));
  } catch (err) {
    console.error('Error submitting feedback:', err);
    notifyNegative(i18n.t('noteAuditor.feedback.notifyFeedbackFail'));
  }
} // submitInaccurateFeedback()

/**
 * Dismisses the audit result by removing it from the session activity. Shows a success or error notification
 * based on the outcome, and refreshes the audit result.
 */
async function dismissAuditResult() {
  try {
    await removeAuditResult();
    forgetAuditRun(props.sessionActivity?.id);
    emit('refresh');
    notifySuccess(i18n.t('noteAuditor.feedback.notifyClearSuccess'));
  } catch (err) {
    console.error('Error dismissing audit result:', err);
    notifyNegative(i18n.t('noteAuditor.feedback.notifyClearFail'));
  }
} // dismissAuditResult()
</script>

<template>
  <audit-verdict-banner
    v-if="auditResultBannerProps"
    v-bind="auditResultBannerProps"
    class="audit-result-card"
  >
    <template #actions>
      <hr-activity-spinner
        v-if="isAuditRunning"
        size="16px"
        :aria-label="i18n.t('noteAuditor.statusRunning')"
      />
      <universal-tooltip
        v-else-if="canRateAudit && hasFeedback"
        :text="i18n.t('noteAuditor.feedback.clearAudit')"
        show-tooltip
      >
        <hr-icon-action-btn
          icon="close"
          :loading="isProcessingFeedback"
          :aria-label="i18n.t('noteAuditor.feedback.clearAudit')"
          @click="dismissAuditResult"
        />
      </universal-tooltip>
      <template v-else-if="canRateAudit">
        <universal-tooltip
          :text="i18n.t('noteAuditor.feedback.accurate')"
          show-tooltip
        >
          <hr-icon-action-btn
            icon="thumb_up"
            :loading="isProcessingFeedback"
            :disable="isShowingInaccurateFeedbackForm"
            :aria-label="i18n.t('noteAuditor.feedback.accurate')"
            @click="submitAccurateFeedback"
          />
        </universal-tooltip>
        <universal-tooltip
          :text="i18n.t('noteAuditor.feedback.inaccurate')"
          :show-tooltip="!isShowingInaccurateFeedbackForm"
        >
          <hr-icon-toggle-btn
            icon="thumb_down"
            selected-icon="thumb_down"
            :selected="isShowingInaccurateFeedbackForm"
            :loading="isProcessingFeedback"
            :aria-label="i18n.t('noteAuditor.feedback.inaccurate')"
            @update:selected="switchInaccurateFeedbackForm"
          />
        </universal-tooltip>
      </template>
    </template>

    <div
      v-if="isShowingInaccurateFeedbackForm && !isAuditRunning"
      class="audit-result-card__section hr-surface"
    >
      <div class="audit-result-card__section-body">
        <div class="audit-result-card__section-heading q-mb-md">
          <hr-icon
            name="thumb_down"
            size="sm"
            class="hr-text-error"
          />
          <div>
            <hr-p class="hr-label-small audit-result-card__label hr-text-on-surface-variant q-mb-xs">
              {{ i18n.t('noteAuditor.feedback.feedbackHeading') }}
            </hr-p>
            <hr-p class="hr-title-small hr-text-on-surface-variant">
              {{ i18n.t('noteAuditor.feedback.commentLabel') }}
            </hr-p>
          </div>
        </div>

        <hr-text-field
          v-model="feedbackComment"
          variant="outlined"
          autofocus
          multiline
          class="full-width q-mb-md"
        />

        <div>
          <hr-p class="hr-label-medium q-mb-sm hr-text-on-surface-variant">
            {{ i18n.t('noteAuditor.feedback.correctedVerdictLabel') }}
          </hr-p>
          <hr-filter-chip-group
            v-model="feedbackCorrectedVerdict"
            :options="correctedVerdictOptions"
            clearable
          />
        </div>
      </div>

      <div class="audit-result-card__actions hr-surface">
        <hr-dismiss-btn
          :label="i18n.t('common.buttonCancel')"
          :disable="isProcessingFeedback"
          @click="cancelInaccurateFeedbackForm"
        />
        <hr-confirm-btn
          :label="i18n.t('noteAuditor.feedback.submitFeedback')"
          :loading="isProcessingFeedback"
          @click="submitInaccurateFeedback"
        />
      </div>
    </div>

    <div
      v-if="hasProposal && learningProposalSection"
      class="audit-result-card__proposal-block hr-surface hr-elevation-1 q-ma-md"
    >
      <div
        class="audit-result-card__section-header"
        :class="learningProposalSection.containerClass"
      >
        <hr-icon
          :name="learningProposalSection.icon"
          size="sm"
        />
        <span class="hr-label-large">{{ learningProposalSection.heading }}</span>
      </div>

      <div class="audit-result-card__section-body hr-surface-container-lowest">
        <hr-p class="hr-title-medium q-mb-md">
          {{ learningProposalTitle }}
        </hr-p>

        <div
          v-if="isNewProposal"
          class="q-mb-sm"
        >
          <hr-p class="audit-result-card__label hr-text-on-surface-variant hr-label-medium q-mb-sm">
            {{ i18n.t('noteAuditor.feedback.newRuleLabel') }}
          </hr-p>
          <hr-text-field
            v-model="editLearningValue"
            variant="outlined"
            :rules="rules.learning"
            multiline
            maxlength="500"
            counter
            lazy-rules
            class="full-width"
          />
        </div>

        <template v-else-if="isUpdateProposal">
          <div>
            <hr-p class="audit-result-card__label hr-text-on-surface-variant hr-label-medium q-mb-sm">
              {{ i18n.t('noteAuditor.feedback.currentRuleLabel') }}
            </hr-p>
            <div class="learning-rule-box hr-surface-variant">
              {{ proposal?.matchedLearning?.content }}
            </div>
          </div>

          <div class="learning-rule-arrow q-mt-sm q-mb-md">
            <hr-icon
              name="arrow_downward"
              size="sm"
            />
          </div>

          <div class="q-mb-sm">
            <hr-p class="audit-result-card__label audit-result-card__label--accent hr-label-medium q-mb-sm">
              {{ i18n.t('noteAuditor.feedback.updatedRuleLabel') }}
            </hr-p>
            <hr-text-field
              v-model="editLearningValue"
              variant="outlined"
              :rules="rules.learning"
              multiline
              maxlength="500"
              counter
              lazy-rules
              class="full-width"
            />
          </div>
        </template>

        <div
          v-else-if="isDeleteProposal"
          class="q-mb-md"
        >
          <hr-p class="audit-result-card__label hr-text-error hr-label-medium q-mb-sm">
            {{ i18n.t('noteAuditor.feedback.ruleBeingRemovedLabel') }}
          </hr-p>
          <div class="learning-rule-box hr-error-container learning-rule-box--removed">
            {{ proposal?.matchedLearning?.content }}
          </div>
        </div>

        <div
          v-if="learningProposalReasoning"
          class="learning-reasoning-box hr-surface-variant"
        >
          <hr-icon
            name="info"
            size="sm"
          />
          <div>
            <hr-p class="audit-result-card__label hr-label-medium q-mb-xs">
              {{ learningProposalReasoningLabel }}
            </hr-p>
            <hr-p class="hr-body-medium q-mb-none">
              {{ learningProposalReasoning }}
            </hr-p>
          </div>
        </div>

        <hr-warning-banner
          v-if="!isDeleteProposal"
          icon="shield"
          class="q-mt-md"
          :message="i18n.t('noteAuditor.feedback.learningPrivacyCheck')"
        />
      </div>

      <div class="audit-result-card__actions hr-surface-container-lowest">
        <hr-dismiss-btn
          :label="i18n.t('common.buttonDismiss')"
          :disable="isProcessingLearningProposal"
          @click="dismissProposal"
        />
        <hr-confirm-btn
          v-if="isNewProposal"
          :label="i18n.t('noteAuditor.feedback.commitLearning')"
          :disable="hasLearningValidationError || isProcessingLearningProposal"
          :loading="isProcessingLearningProposal"
          @click="approveNewProposal"
        />
        <hr-confirm-btn
          v-else-if="isUpdateProposal"
          :label="i18n.t('noteAuditor.feedback.updateLearning')"
          :disable="hasLearningValidationError || isProcessingLearningProposal || !isMatchedLearningValid"
          :loading="isProcessingLearningProposal"
          @click="approveUpdateProposal"
        />
        <hr-destructive-btn
          v-else-if="isDeleteProposal"
          :label="i18n.t('noteAuditor.feedback.deleteLearning')"
          :disable="isProcessingLearningProposal || !isMatchedLearningValid"
          :loading="isProcessingLearningProposal"
          @click="approveDeleteProposal"
        />
      </div>
    </div>
  </audit-verdict-banner>
</template>

<style lang="scss" scoped>
.audit-result-card {
  &__section {
    &-header {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 16px;
    }

    &-body {
      display: flex;
      flex-direction: column;
      padding: 16px;
    }

    &-heading {
      display: flex;
      align-items: flex-start;
      gap: 12px;
    }
  }

  &__proposal-block {
    border-radius: var(--app-shape-corner-large, 16px);
    overflow: hidden;
  }

  &__label {
    text-transform: uppercase;

    &--accent {
      color: var(--app-on-tertiary-container);
    }
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 8px 16px 16px;
  }
}

.learning-rule-box {
  padding: 12px 16px;
  border-radius: var(--app-shape-corner-medium, 12px);

  &--removed {
    border: 1px solid var(--app-error);
    text-decoration: line-through;
  }
}

.learning-rule-arrow {
  display: flex;
  justify-content: center;
  color: var(--app-on-surface-variant);
}

.learning-reasoning-box {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  border-radius: var(--app-shape-corner-medium, 12px);
}
</style>
